// lib/money.ts — единый модуль суммы сделки (U-32: нормализация + валидация).
// Паттерн Strategy: алгоритм нормализации и правила валидации выделены в один модуль,
// обе формы (DealNewPage, DealEditDialog) используют одну стратегию — исключает дубль
// и рассинхронизацию правил (ранее: isFiniteAmount в DealNewPage vs z.coerce в DealEditDialog).
//
// Контракт:
//   - normalizeAmountInput — строка-представление суммы: убирает ведущие нули
//     (020000 → 20000, 00.5 → 0.5), '0' ОСТАЁТСЯ '0' (ошибка ловится на submit),
//     '' (и пробелы) = «не введено»;
//   - parseAmount — 'не введено'/не-число/бесконечность → null, иначе number;
//   - amountSchema — zod-поле формы: пусто → «Укажите сумму», не число → «Укажите сумму
//     числом», <= 0 → «Сумма должна быть больше 0» (см. Открытый вопрос №1 — Q17).
import { z } from 'zod';
import { t } from '@/i18n';

/**
 * Убирает ведущие нули: 020000 → 20000, 00.5 → 0.5, 000 → 0; '0' сохраняется;
 * тримовые пробелы удаляются; '' означает «не введено».
 */
export function normalizeAmountInput(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  return trimmed.replace(/^0+(?=\d)/, '');
}

/** Парсит нормализованную строку: '' → null; не-число/бесконечность → null; иначе number. */
export function parseAmount(raw: string): number | null {
  const normalized = normalizeAmountInput(raw);
  if (normalized === '') return null;
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
}

/**
 * Zod-схема поля «Сумма» (общая для создания и редактирования сделки).
 * Правила: пустая строка → «Укажите сумму»; не число/бесконечное → «Укажите сумму числом»;
 * 0 и отрицательные → «Сумма должна быть больше 0» (см. Открытый вопрос №1 — Q17).
 * Схема оперирует строкой (из поля ввода) — число в payload даёт parseAmount после прохождения refine;
 * конечность гарантируется parseAmount (Infinity/NaN → null).
 */
export const amountSchema = z
  .string()
  .refine((v) => v.trim() !== '', { message: t('crm.deals.amountRequired') })
  .refine((v) => parseAmount(v) !== null, { message: t('crm.deals.amountInvalid') })
  .refine((v) => {
    const n = parseAmount(v);
    return n !== null && n > 0;
  }, { message: t('crm.deals.amountPositive') });
